import React, {ReactNode} from 'react'
import {FormattedMessage} from 'react-intl'
import {Modal} from '../../../modal'
import {Box, useDisclosure, Text, Center} from '../../../../vanilla'
import {nutrientsTableBodyRow, nutrientsTableRowCell} from './styles.css'

type TextVariant = React.ComponentProps<typeof Text>['variant']
type TextWeight = React.ComponentProps<typeof Text>['weight']

interface NutritionalModalProps extends ReturnType<typeof useDisclosure> {
  title?: ReactNode
  nutritionInformation: {headings: string[]; nutrients: Nutrient[]}
}

export const NutritionalModal = (props: NutritionalModalProps) => {
  const {title, nutritionInformation, ...rest} = props

  return (
    <Modal
      title={title ?? <FormattedMessage defaultMessage="Nutritional Table" />}
      size="nutritional"
      {...rest}
      data-test-selector="nutritional-table-modal-component"
    >
      <Box paddingX="12px" paddingTop="20px" overflowX="auto" data-test-selector="nutritional-table-modal">
        <NutritionalTable data={nutritionInformation} />
      </Box>
    </Modal>
  )
}

interface Nutrient {
  name: string
  row: number
  values: string[]
}

interface NutritionalTableProps {
  data: {headings: string[]; nutrients: Nutrient[]}
  styleChanges?: {
    headerTextVariant?: TextVariant
    headerTextWeight?: TextWeight
    tableTextVariant?: TextVariant
    tableRowStyling?: React.CSSProperties
  }
}
export const NutritionalTable = ({data, styleChanges}: NutritionalTableProps) => {
  if (!data || !Object.keys(data).length) {
    return (
      <Center>
        <Text>No Nutritional data</Text>
      </Center>
    )
  }

  function formatNutritionalValue(nutritionalValue: string) {
    try {
      return nutritionalValue.replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    } catch (error) {
      return false
    }
  }

  const headerTextVariant = styleChanges?.headerTextVariant ? {variant: styleChanges.headerTextVariant} : {variant: 'unstyled' as const}
  const headerTextWeight = styleChanges?.headerTextWeight ? {fontWeight: styleChanges.headerTextWeight} : {}
  const tableTextVariant = styleChanges?.tableTextVariant ? {variant: styleChanges.tableTextVariant} : {variant: 'unstyled' as const}
  const tableRowStyling = styleChanges?.tableRowStyling ? {style: {...styleChanges.tableRowStyling}} : {}

  return (
    <Box as="table" width="full" maxWidth="full" marginBottom="16px">
      <Box as="thead">
        <Box as="tr">
          <Box as="th" scope="col" className={nutrientsTableRowCell}>
            <Text as="span" style={{color: '#4a4b4d'}} fontSize="md" {...headerTextVariant} {...headerTextWeight}>
              Typical Values
            </Text>
          </Box>
          {data?.headings?.map((heading) => (
            <Box
              as="th"
              key={`nutritional-heading-${heading}`}
              scope="col"
              className={nutrientsTableRowCell}
            >
              <Text as="span" style={{color: '#4a4b4d'}} fontSize="md" {...headerTextVariant} {...headerTextWeight}>
                {heading}
              </Text>
            </Box>
          ))}
        </Box>
      </Box>

      <Box as="tbody">
        {[...data.nutrients]
          .sort(({row: row1}, {row: row2}) => row1 - row2)
          .map((nutrient, idx) => (
            <Box as="tr" className={nutrientsTableBodyRow} {...tableRowStyling}>
              <Box as="td" className={nutrientsTableRowCell}>
                <Text
                  as="span"
                  color="gray1000"
                  fontSize="md"
                  {...tableTextVariant}
                  fontWeight="normal"
                >
                  {nutrient.name}
                </Text>
              </Box>
              {nutrient.values.map((nutrientValue, valueIdx) => {
                const formattedNutritionalValue = formatNutritionalValue(nutrientValue)
                return (
                  <Box
                    key={`nutrient-value-${idx}-${nutrient.name}${valueIdx}`}
                    as="td"
                    className={nutrientsTableRowCell}
                  >
                    <Text
                      as="span"
                      fontSize="md"
                      {...tableTextVariant}
                      color="gray1000"
                      fontWeight="normal"
                    >
                      {formattedNutritionalValue === false
                        ? nutrientValue
                        : formattedNutritionalValue}
                    </Text>
                  </Box>
                )
              })}
            </Box>
          ))}
      </Box>
    </Box>
  )
}
